// eslint-disable-next-line
import React, { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Button, message } from "antd"
import { Link } from 'react-router-dom'
import { Formik } from "formik"
import { Form, Input } from "formik-antd"
import * as yup from 'yup'
import styled, { keyframes } from "styled-components"

import { footerText } from "util/config"
import { AppState } from 'ReducerState'
import { hideMessage, verifyOTP, resetProcessStatus } from "appRedux/actions/Auth"
import { isEmpty } from 'lodash'

/* ── Animations ── */
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`

/* ── Page background ── */
const PageWrap = styled.div`
  display: flex;
  min-height: 100vh;
  width: 100%;
  align-items: center;
  justify-content: center;
  background: #f4f7f9;
  position: relative;
  overflow: hidden;
  padding: 32px 16px;
`

/* ── Card ── */
const Card = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 440px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.04);
  padding: 40px;
  animation: ${fadeInUp} 0.4s cubic-bezier(0.22, 1, 0.36, 1) both;

  @media (max-width: 480px) {
    padding: 32px 24px;
  }
`

/* ── Logo row ── */
const LogoRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 24px;
`

const LogoImg = styled.img`
  width: 34px;
  height: 34px;
  object-fit: contain;
`

const BrandName = styled.span`
  font-size: 22px;
  font-weight: 800;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: #111827;
`

const DividerLine = styled.div`
  width: 100%;
  height: 1px;
  background: #f3f4f6;
  margin-bottom: 24px;
`

const WelcomeTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  text-align: center;
  margin: 0 0 6px;
`

const WelcomeSubtitle = styled.p`
  font-size: 12px;
  color: #6b7280;
  text-align: center;
  margin: 0 0 4px;
`

const HighlightedText = styled.span`
  color: #3b82f6;
  font-weight: 600;
`

/* ── Form body ── */
const FieldLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 6px;
`

const FieldWrap = styled.div`
  margin-top: 24px;
  margin-bottom: 16px;

  .ant-input {
    height: 42px;
    border-radius: 6px;
    background: #f4f5f7;
    border: 1px solid #e2e8f0;
    transition: all 0.2s;
    text-align: center;
    font-size: 18px;
    letter-spacing: 2px;
  }

  .ant-input:hover {
    border-color: #cbd5e1;
  }

  .ant-input:focus {
    background: #eff6ff;
    border-color: #60a5fa;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
  }
`

export const DivBlockWarning = styled.div`
  font-size: 12px;
  font-style: italic;
  color: #ef4444;
  font-weight: 400;
  margin-top: 4px;
  text-align: center;
`

const ActionRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`

const SubmitBtn = styled(Button)`
  flex: 1;
  height: 42px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 6px;
  background: #3b82f6 !important;
  border-color: #3b82f6 !important;
  box-shadow: none;

  &:hover {
    background: #2563eb !important;
    border-color: #2563eb !important;
  }
`

const FooterBlock = styled.div`
  margin-top: 32px;
  text-align: center;
`

const FooterLine = styled.div`
  font-size: 11px;
  color: #9ca3af;
  line-height: 1.7;
`

const OTPVerify = ({ history }: any) => {

  const [initialData] = useState<any>({ otp_number: undefined, secret_key: undefined })
  const { auth } = useSelector((state: AppState) => state)
  const { showMessage, loader, alertMessage, processStatus, otpGenerate, activateUID } = auth

  const dispatch = useDispatch()
  const mounted = useRef<any>(undefined)

  useEffect(() => {
    if (isEmpty(otpGenerate)) history.push("/signin")
  }, [otpGenerate, history])

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true
    } else {
      if (processStatus) {
        const { activate_uid, grant_type } = activateUID
        const urlRedirect = `/activate/?uid=${activate_uid}&gt=${grant_type}&ac=OTP`
        setTimeout(() => { history.push(urlRedirect) }, 1000)
      } else if (showMessage) {
        message.error((alertMessage as any).toString())
        setTimeout(() => { dispatch(hideMessage()) }, 100)
      }
      dispatch(resetProcessStatus())
    }
  }, [processStatus, dispatch, showMessage, alertMessage, activateUID, history])

  const handleSubmit = (value: any) => {
    const { secret_key } = otpGenerate
    const dataInfo = { otp_number: value.otp_verify, secret_key }
    dispatch(verifyOTP(dataInfo))
  }

  let schema = yup.object({
    otp_verify: yup.string().required("Please input OTP number").matches(RegExp("^[0-9]*$"), "Please input numbers only")
  })

  return (
    <PageWrap>
      <Card>
        <LogoRow>
          <LogoImg src={`${process.env.PUBLIC_URL}/icon-512x512.png`} alt="FROMAS" />
          <BrandName>FROMAS</BrandName>
        </LogoRow>
        
        <DividerLine />

        <WelcomeTitle>OTP Verification</WelcomeTitle>
        <WelcomeSubtitle>
          Please wait while we send the OTP to you.
        </WelcomeSubtitle>
        <WelcomeSubtitle>
          Ref no: <HighlightedText>{(otpGenerate as any)?.referent_no}</HighlightedText>
        </WelcomeSubtitle>
        <WelcomeSubtitle style={{ color: '#ef4444', marginTop: 8 }}>
          This code will expire in 5 minutes
        </WelcomeSubtitle>

        <Formik
          enableReinitialize
          initialValues={initialData}
          validationSchema={schema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched }) =>
            <Form layout="vertical">
              <FieldWrap>
                <FieldLabel style={{ textAlign: 'center' }}>Enter OTP Number</FieldLabel>
                <Input name='otp_verify' type="text" placeholder="XXXXXX" maxLength={6} />
                {errors?.otp_verify && touched.otp_verify && (
                  <DivBlockWarning>
                    {Array.isArray(errors.otp_verify) 
                      ? errors.otp_verify.join(', ') 
                      : typeof errors.otp_verify === 'string' 
                        ? errors.otp_verify 
                        : String(errors.otp_verify)}
                  </DivBlockWarning>
                )}
              </FieldWrap>

              <ActionRow>
                <SubmitBtn type="primary" htmlType="submit" loading={loader}>
                  Verify OTP
                </SubmitBtn>
              </ActionRow>
            </Form>
          }
        </Formik>

        <FooterBlock>
          <FooterLine>Version: {process.env.REACT_APP_VERSION}</FooterLine>
          <FooterLine>{footerText.copyright}</FooterLine>
        </FooterBlock>
      </Card>
    </PageWrap>
  )
}
export default OTPVerify


